import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { toast } from 'sonner';
import { Button } from '../../../../../../components/Button';
import { TextInputFormField } from '../../../../../../components/TextInputFormField';
import { GripVerticalIcon } from '../../../../../../components/svgs/icons/GripVerticalIcon';
import { XIcon } from '../../../../../../components/svgs/icons/XIcon';
import { charmGray } from '../../../../../../styles/colors';
import { bs } from '../../../../../../styles/styles';
import { styleSheet } from '../styles';

interface FieldOptionsProps {
	field: Api.IFormField<string>;
	questionIndex: number;
	onOptionChange: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;
	onRemoveOption: (index: number) => void;
	onAddOption: () => void;
	/**
	 * Moves the option at `fromIndex` to `toIndex`. Omit to render a static list with no drag handles.
	 * @param fromIndex Index the option is being dragged from.
	 * @param toIndex Index the option is being dropped at.
	 */
	onMoveOption?: (fromIndex: number, toIndex: number) => void;
	onAfterItem?: (optionLabel: string) => React.ReactNode;
	disabled?: boolean;
}

const FieldOptionsInner = React.memo<FieldOptionsProps>(
	({
		field,
		questionIndex,
		onOptionChange,
		onRemoveOption,
		onAddOption,
		onMoveOption,
		onAfterItem,
		disabled = false,
	}) => {
		// Memoize expensive calculations
		const fieldOptions = React.useMemo(() => field?.options || [], [field?.options]);
		const fieldType = field.fieldType;
		const isMultipleOptions = fieldType === Api.FormFieldType.MultipleOptions;
		const isOption = fieldType === Api.FormFieldType.Option;
		const showRemoveButtons = fieldOptions.length > 2;

		// react-beautiful-dnd keys its registry on draggableId and its droppableId, so ids that fall back
		// to a position or to '' would collide across fields or shift on the very drop they are tracking.
		const canReorder = React.useMemo(
			() =>
				Boolean(onMoveOption) &&
				!disabled &&
				fieldOptions.length > 1 &&
				Boolean(field?.id) &&
				fieldOptions.every(option => Boolean(option.id)),
			[onMoveOption, disabled, fieldOptions, field?.id]
		);

		const wrapperClassName = css(styleSheet.optionsWrapper);

		// Memoize add button className
		const addButtonClassName = React.useMemo(
			() => css(styleSheet.addOptionButton, isMultipleOptions ? styleSheet.addOptionButtonLeftPadding : null),
			[isMultipleOptions]
		);

		const handleDragEnd = React.useCallback(
			(result: DropResult) => {
				if (!result.destination || result.source.index === result.destination.index) {
					return;
				}
				onMoveOption?.(result.source.index, result.destination.index);
			},
			[onMoveOption]
		);

		if (!isOption && !isMultipleOptions) {
			return null;
		}

		const renderRowChildren = (
			fieldOption: Api.CustomFormFieldOption,
			optionIndex: number,
			optionKey: string,
			dragHandle: React.ReactNode
		) => {
			const fieldOptionValue = fieldOption.label;
			const hasValueAndAfterInputItem = fieldOptionValue && onAfterItem;
			const placeholder = isOption ? `Option ${optionIndex + 1}` : `Response ${optionIndex + 1}`;

			return (
				<>
					{dragHandle}
					{isMultipleOptions ? <div className={css(styleSheet.fakeCheckbox)} /> : null}
					<div className={css(bs.fullWidth)}>
						<div className={css(bs.fullWidth, bs.relative)}>
							<TextInputFormField
								key={optionKey}
								labelStyles={[styleSheet.label]}
								placeholder={placeholder}
								onChange={e => onOptionChange(e, optionIndex)}
								onKeyDown={e => {
									if (e.key === ',') {
										e.preventDefault();
										toast.error('Commas are not allowed in option labels');
									}
								}}
								onPaste={e => {
									if (e.clipboardData.getData('text').includes(',')) {
										e.preventDefault();
										toast.error('Commas are not allowed in option labels');
									}
								}}
								type='text'
								styles={[styleSheet.questionInput, hasValueAndAfterInputItem ? styleSheet.afterInputItem : undefined]}
								inputId={optionKey}
								value={fieldOptionValue}
								disabled={disabled}
							/>
							{hasValueAndAfterInputItem ? onAfterItem(fieldOptionValue) : null}
						</div>
					</div>
					{showRemoveButtons && !disabled ? (
						<div>
							<Button
								kind='custom'
								type='button'
								className={css(styleSheet.removeOptionButton)}
								label={
									<>
										<span className={css(styleSheet.removeOptionButtonText)}>Remove Option</span>
										<XIcon color={charmGray} width={16} height={16} />
									</>
								}
								onClick={() => onRemoveOption(optionIndex)}
								data-testid='remove-option'
							/>
						</div>
					) : null}
				</>
			);
		};

		const renderRow = (fieldOption: Api.CustomFormFieldOption, optionIndex: number) => {
			const optionId = fieldOption.id;
			// Keyed on the option's own id so a reorder moves rows rather than remounting the inputs
			// between the source and destination positions, which would drop focus mid-edit.
			const optionKey = `edit-custom-${fieldOption.fieldId}-question-${questionIndex}-input-${optionId ?? optionIndex}`;

			if (!canReorder || !optionId) {
				return (
					<div className={css(bs.flex, bs.itemsCenter, bs.gap2)} key={optionKey}>
						{renderRowChildren(fieldOption, optionIndex, optionKey, null)}
					</div>
				);
			}

			return (
				<Draggable draggableId={optionId} index={optionIndex} key={optionId}>
					{(provided, snapshot) => (
						<div
							ref={provided.innerRef}
							{...provided.draggableProps}
							className={css(
								bs.flex,
								bs.itemsCenter,
								bs.gap2,
								snapshot.isDragging ? styleSheet.optionRowDragging : undefined
							)}
						>
							{renderRowChildren(
								fieldOption,
								optionIndex,
								optionKey,
								<div
									className={css(styleSheet.optionDragHandle)}
									{...provided.dragHandleProps}
									aria-label={`Reorder option ${optionIndex + 1}`}
								>
									<GripVerticalIcon width={16} height={16} />
								</div>
							)}
						</div>
					)}
				</Draggable>
			);
		};

		const addOptionButton =
			fieldOptions.length && !disabled ? (
				<Button
					kind='custom'
					type='button'
					label='+ Add Option'
					className={addButtonClassName}
					onClick={() => onAddOption()}
				/>
			) : null;

		if (!canReorder) {
			return (
				<div className={wrapperClassName}>
					{fieldOptions.map(renderRow)}
					{addOptionButton}
				</div>
			);
		}

		return (
			<DragDropContext onDragEnd={handleDragEnd}>
				<div className={wrapperClassName}>
					<Droppable direction='vertical' droppableId={`field-options-${field.id}`}>
						{(droppableProvided: DroppableProvided) => (
							<div
								className={css(styleSheet.optionRows)}
								ref={droppableProvided.innerRef}
								{...droppableProvided.droppableProps}
							>
								{fieldOptions.map(renderRow)}
								{droppableProvided.placeholder}
							</div>
						)}
					</Droppable>
					{addOptionButton}
				</div>
			</DragDropContext>
		);
	}
);

FieldOptionsInner.displayName = 'FieldOptionsInner';

export const FieldOptions = FieldOptionsInner;
